import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RatingItem } from '@goyone/shared';
import { api, qs } from '../lib/api';
import { relative } from '../lib/format';
import { Icon } from './Icon';
import { useToast } from './Toast';

/**
 * زرّ «رابط تقييم العميل» — واحدٌ في كل النظام.
 *
 * يولّد الرابط (أو يعيد المفتوح القائم) وينسخه، ويعرض حال آخر تقييمٍ
 * بصدق: قُيّم بنجومه، أو أُرسل ولم يُجَب، أو لم يُطلب بعد. عناصرُ ما
 * يُسأل عنه العميل تُضبط من الإعدادات لكل سياق.
 */

type Kind = 'ACCOUNT' | 'DEAL' | 'PROJECT' | 'TASK';

interface LatestRating {
  score: number | null;
  scores: Record<string, number> | null;
  submittedAt: string | null;
  createdAt: string;
  comment: string | null;
}

export function RatingLinkButton({
  kind,
  entityId,
  className = 'btn btn-sm',
}: {
  kind: Kind;
  entityId: string;
  className?: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const state = useQuery({
    queryKey: ['rating', kind, entityId],
    queryFn: () =>
      api<{ latest: LatestRating | null }>(`/ratings/for-entity${qs({ kind, entityId })}`),
  });

  const mint = useMutation({
    mutationFn: () =>
      api<{ token: string }>('/ratings/request', { method: 'POST', body: { kind, entityId } }),
    onSuccess: async (res) => {
      const url = `${window.location.origin}/rate/${res.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast('نُسخ رابط التقييم — أرسله للعميل من واتساب أو البريد أو SMS');
      } catch {
        window.prompt('انسخ الرابط:', url);
      }
      void queryClient.invalidateQueries({ queryKey: ['rating', kind, entityId] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر توليد الرابط.'),
  });

  const latest = state.data?.latest;

  return (
    <span className="row" style={{ gap: 'var(--sp-2)' }}>
      {/* حال آخر تقييم — معلومة لا زينة. */}
      {latest?.score != null && (
        <span className="tiny mute nowrap" title="آخر تقييم من العميل">
          {latest.score} ★
        </span>
      )}
      {latest && latest.score == null && (
        <span className="tiny mute nowrap">أُرسل — لم يُجب بعد</span>
      )}

      <button
        className={className}
        disabled={mint.isPending}
        onClick={() => mint.mutate()}
        title="انسخ رابط تقييمٍ يفتحه العميل بلا تسجيل"
      >
        <Icon name="star" size={14} />
        رابط تقييم العميل
      </button>
    </span>
  );
}

/* ==========================================================================
   عرضُ التقييم كاملًا — النجوم والنص والعناصر
   ========================================================================== */

/**
 * كتلة تقييم كيانٍ واحد: آخرُ رأيٍ تركه العميل بكامله — النجوم الكلّية
 * والتعليق ودرجات العناصر بأسمائها من الإعدادات — وزرُّ طلب التقييم.
 *
 * `canRequest` يخفي الزرّ لمن يقرأ فقط، والعرض للجميع: رأي العميل
 * معلومة فريق لا سرّ مدير.
 */
export function EntityRating({
  kind,
  entityId,
  canRequest = true,
}: {
  kind: Kind;
  entityId: string;
  canRequest?: boolean;
}) {
  const state = useQuery({
    queryKey: ['rating', kind, entityId],
    queryFn: () =>
      api<{ latest: LatestRating | null }>(`/ratings/for-entity${qs({ kind, entityId })}`),
  });

  /* أسماء العناصر — لعرض درجاتها المخزّنة بمعرّفاتها. */
  const config = useQuery({
    queryKey: ['ratings', 'config'],
    queryFn: () => api<{ items: Record<string, RatingItem[]> }>('/ratings/config'),
    staleTime: 5 * 60_000,
  });

  const latest = state.data?.latest;
  const items = config.data?.items[kind] ?? [];
  const labelOf = (id: string) => items.find((i) => i.id === id)?.label ?? null;

  return (
    <div className="col" style={{ gap: 'var(--sp-2)' }}>
      {latest?.score != null && (
        <div className="rating-view">
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <RatingStars value={latest.score} />
            <span className="tiny mute">
              {latest.submittedAt ? relative(latest.submittedAt) : ''}
            </span>
          </div>

          {latest.scores && Object.keys(latest.scores).length > 0 && (
            <div className="rating-view-items">
              {Object.entries(latest.scores).map(([id, v]) => {
                const label = labelOf(id);
                if (!label) return null;
                return (
                  <div key={id} className="row" style={{ gap: 'var(--sp-2)' }}>
                    <span className="tiny">{label}</span>
                    <span style={{ marginInlineStart: 'auto' }}>
                      <RatingStars value={v} size={12} />
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {latest.comment && <p className="small cc-feedback">{latest.comment}</p>}
        </div>
      )}

      {latest && latest.score == null && (
        <p className="tiny mute">أُرسل رابط التقييم — لم يُجب العميل بعد.</p>
      )}
      {state.isSuccess && !latest && (
        <p className="tiny mute">لم يُطلب تقييم العميل بعد.</p>
      )}

      {canRequest && <RequestOnly kind={kind} entityId={entityId} />}
    </div>
  );
}

/** نجومٌ للعرض — ممتلئة بقدر التقييم. */
export function RatingStars({ value, size = 15 }: { value: number; size?: number }) {
  return (
    <span className="cc-stars" title={`${value} من ٥`} aria-label={`${value} من ٥`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`cc-star${value >= n ? ' on' : ''}`}>
          <Icon name="star" size={size} />
        </span>
      ))}
    </span>
  );
}

/** زرّ الطلب وحده — للكتلة التي تعرض الحال بنفسها فلا تكرّره. */
function RequestOnly({ kind, entityId }: { kind: Kind; entityId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mint = useMutation({
    mutationFn: () =>
      api<{ token: string }>('/ratings/request', { method: 'POST', body: { kind, entityId } }),
    onSuccess: async (res) => {
      const url = `${window.location.origin}/rate/${res.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast('نُسخ رابط التقييم — أرسله للعميل من واتساب أو البريد أو SMS');
      } catch {
        window.prompt('انسخ الرابط:', url);
      }
      void queryClient.invalidateQueries({ queryKey: ['rating', kind, entityId] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر توليد الرابط.'),
  });

  return (
    <span>
      <button
        className="btn btn-sm"
        disabled={mint.isPending}
        onClick={() => mint.mutate()}
        title="انسخ رابط تقييمٍ يفتحه العميل بلا تسجيل"
      >
        <Icon name="star" size={14} />
        رابط تقييم العميل
      </button>
    </span>
  );
}
